import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  Event as EventIcon,
  Flag as FlagIcon,
  Info as InfoIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const DEFAULT_SETTINGS = {
  startYear: 0,
  endYear: 1000,
  yearLabel: 'CE',
  displayYears: true
};

const TimelineEventList = ({ 
  events = [], 
  eras = [], 
  settings = DEFAULT_SETTINGS,
  onEditEvent, 
  onDeleteEvent 
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const ember = isDarkMode ? '#8ec5ff' : '#1e88e5';
  const emberBright = isDarkMode ? '#d2e9ff' : '#64b5f6';
  const emberGlow = isDarkMode ? 'rgba(130, 177, 255, 0.28)' : 'rgba(30, 136, 229, 0.12)';
  const emberBorder = isDarkMode ? 'rgba(144, 202, 249, 0.4)' : 'rgba(21, 101, 192, 0.2)';
  const toolbarPaperSx = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 2,
    p: 2.5,
    mb: 3,
    borderRadius: 2,
    border: `1px solid ${emberBorder}`,
    background: isDarkMode
      ? 'linear-gradient(135deg, rgba(5,12,30,0.92), rgba(14,32,58,0.94))'
      : 'linear-gradient(135deg, rgba(232,244,255,0.96), rgba(245,250,255,0.98))',
    boxShadow: isDarkMode
      ? '0 15px 40px rgba(0,0,0,0.55)'
      : '0 15px 40px rgba(30, 136, 229, 0.18)',
    backdropFilter: 'blur(8px)',
    alignItems: 'center'
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [eraFilter, setEraFilter] = useState('all');
  const [importanceFilter, setImportanceFilter] = useState('all');
  const [orderBy, setOrderBy] = useState('year');
  const [order, setOrder] = useState('asc');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleEraFilterChange = (event) => {
    setEraFilter(event.target.value);
  };

  const handleImportanceFilterChange = (event) => {
    setImportanceFilter(event.target.value);
  };

  const handleMenuOpen = (event, eventItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedEvent(eventItem);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEvent(null);
  };

  const handleEdit = () => {
    onEditEvent(selectedEvent);
    handleMenuClose();
  };

  const handleDelete = () => {
    onDeleteEvent(selectedEvent.id);
    handleMenuClose();
  };

  // Get era name by id
  const getEraName = (eraId) => {
    const era = eras.find(era => era.id === eraId);
    return era ? era.name : 'Unknown Era';
  };

  // Get era color by id
  const getEraColor = (eraId) => {
    const era = eras.find(era => era.id === eraId);
    return era ? era.color : '#cccccc';
  };

  // Format year with label
  const formatYear = (year) => {
    if (year === undefined || year === null) return '';
    
    const yearNum = parseInt(year);
    if (isNaN(yearNum)) return year;
    
    if (settings.yearLabel) {
      if (yearNum < 0) {
        return `${Math.abs(yearNum)} ${settings.yearLabel.replace('CE', 'BCE')}`;
      } else {
        return `${yearNum} ${settings.yearLabel}`;
      }
    } else {
      return year.toString();
    }
  };

  // Filter and sort events
  const filteredEvents = events.filter(event => {
    const matchesSearch = 
      event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (event.description && event.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (event.date && event.date.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (event.year !== undefined && event.year.toString().includes(searchTerm));
    
    const matchesEra = eraFilter === 'all' || event.era === eraFilter;
    
    const matchesImportance = importanceFilter === 'all' || event.importance === importanceFilter;
    
    return matchesSearch && matchesEra && matchesImportance;
  });

  const sortedEvents = filteredEvents.sort((a, b) => {
    // Custom sorting logic based on the selected column
    if (orderBy === 'importance') {
      const importanceOrder = { high: 3, medium: 2, low: 1 };
      const valueA = importanceOrder[a.importance] || 0;
      const valueB = importanceOrder[b.importance] || 0;
      return (order === 'asc' ? 1 : -1) * (valueA - valueB);
    }
    
    if (orderBy === 'year') {
      const yearA = parseInt(a.year) || 0;
      const yearB = parseInt(b.year) || 0;
      return (order === 'asc' ? 1 : -1) * (yearA - yearB);
    }
    
    // For other columns, use string comparison
    const valueA = (a[orderBy] || '').toString().toLowerCase();
    const valueB = (b[orderBy] || '').toString().toLowerCase();
    
    if (valueA < valueB) {
      return order === 'asc' ? -1 : 1;
    }
    if (valueA > valueB) {
      return order === 'asc' ? 1 : -1;
    }
    return 0;
  });

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="overline" sx={{ letterSpacing: 3, color: emberBright }}>
        EVENT LEDGER
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 600, color: isDarkMode ? '#e3f2fd' : '#0d2756' }}>
        Chronology Intelligence Sheet
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Audit every milestone with filters, quick sort, and contextual color coding that mirrors the cinematic timeline.
      </Typography>

      <Paper sx={toolbarPaperSx} elevation={0}>
        <TextField
          placeholder="Search events..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearch}
          sx={{
            flexGrow: 1,
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              background: isDarkMode ? 'rgba(5,10,25,0.7)' : '#fff',
              '& fieldset': { borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: emberBorder },
              '&.Mui-focused fieldset': { borderColor: ember }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />

        <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="era-filter-label">Era</InputLabel>
          <Select
            labelId="era-filter-label"
            value={eraFilter}
            onChange={handleEraFilterChange}
            label="Era"
            sx={{ borderRadius: 1 }}
          >
            <MenuItem value="all">All Eras</MenuItem>
            {eras.map((era) => (
              <MenuItem key={era.id} value={era.id}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {era.color && (
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: era.color,
                        mr: 1,
                        border: '1px solid rgba(0,0,0,0.15)'
                      }}
                    />
                  )}
                  {era.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="importance-filter-label">Importance</InputLabel>
          <Select
            labelId="importance-filter-label"
            value={importanceFilter}
            onChange={handleImportanceFilterChange}
            label="Importance"
            sx={{ borderRadius: 1 }}
          >
            <MenuItem value="all">All Levels</MenuItem>
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
          </Select>
        </FormControl>
      </Paper>
      
      <TableContainer
        component={Paper}
        sx={{
          mb: 4,
          borderRadius: 2,
          border: `1px solid ${emberBorder}`,
          boxShadow: isDarkMode ? '0 25px 45px rgba(0,0,0,0.6)' : '0 25px 45px rgba(21,101,192,0.18)',
          overflow: 'hidden'
        }}
      >
        <Table size="small">
          <TableHead
            sx={{
              background: isDarkMode
                ? 'linear-gradient(120deg, rgba(25,118,210,0.45), rgba(8,19,40,0.85))'
                : 'linear-gradient(120deg, #64b5f6, #1e88e5)',
              '& .MuiTableCell-root': {
                color: isDarkMode ? '#fff5ea' : '#fff',
                fontWeight: 600,
                letterSpacing: 0.4
              }
            }}
          >
            <TableRow>
              <TableCell sx={{ color: 'primary.contrastText' }}>
                <TableSortLabel
                  active={orderBy === 'title'}
                  direction={orderBy === 'title' ? order : 'asc'}
                  onClick={() => handleRequestSort('title')}
                  sx={{ 
                    color: 'inherit',
                    '&.MuiTableSortLabel-active': {
                      color: 'inherit',
                    },
                    '& .MuiTableSortLabel-icon': {
                      color: 'inherit !important',
                    }
                  }}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ color: 'primary.contrastText' }}>
                <TableSortLabel
                  active={orderBy === 'year'}
                  direction={orderBy === 'year' ? order : 'asc'}
                  onClick={() => handleRequestSort('year')}
                  sx={{ 
                    color: 'inherit',
                    '&.MuiTableSortLabel-active': {
                      color: 'inherit',
                    },
                    '& .MuiTableSortLabel-icon': {
                      color: 'inherit !important',
                    }
                  }}
                >
                  Year
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ color: 'primary.contrastText' }}>
                <TableSortLabel
                  active={orderBy === 'era'}
                  direction={orderBy === 'era' ? order : 'asc'}
                  onClick={() => handleRequestSort('era')}
                  sx={{ 
                    color: 'inherit',
                    '&.MuiTableSortLabel-active': {
                      color: 'inherit',
                    },
                    '& .MuiTableSortLabel-icon': {
                      color: 'inherit !important',
                    }
                  }}
                >
                  Era
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ color: 'primary.contrastText' }}>
                <TableSortLabel
                  active={orderBy === 'importance'}
                  direction={orderBy === 'importance' ? order : 'asc'}
                  onClick={() => handleRequestSort('importance')}
                  sx={{ 
                    color: 'inherit',
                    '&.MuiTableSortLabel-active': {
                      color: 'inherit',
                    },
                    '& .MuiTableSortLabel-icon': {
                      color: 'inherit !important',
                    }
                  }}
                >
                  Importance
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ color: 'primary.contrastText' }}>Description</TableCell>
              <TableCell sx={{ color: 'primary.contrastText' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              '& .MuiTableRow-root': {
                '&:nth-of-type(odd)': {
                  background: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(227,242,253,0.65)'
                }
              }
            }}
          >
            {sortedEvents.length > 0 ? (
              sortedEvents.map((event) => (
                <TableRow 
                  key={event.id}
                  sx={{ 
                    '&:hover': { backgroundColor: isDarkMode ? 'rgba(142,197,255,0.12)' : 'rgba(187,222,251,0.6)' },
                    transition: 'background 0.2s ease'
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {event.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatYear(event.year)}
                      {event.date && ` (${event.date})`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {event.era && (
                      <Chip 
                        size="small"
                        label={getEraName(event.era)}
                        sx={{ 
                          backgroundColor: `${getEraColor(event.era)}22`,
                          color: getEraColor(event.era),
                          borderRadius: '999px',
                          border: `1px solid ${getEraColor(event.era)}55`,
                          fontWeight: 600
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      size="small"
                      label={event.importance ? event.importance.charAt(0).toUpperCase() + event.importance.slice(1) : 'Medium'}
                      sx={{
                        borderRadius: '999px',
                        fontWeight: 600,
                        backgroundColor:
                          event.importance === 'high'
                            ? 'rgba(30, 136, 229, 0.2)'
                            : event.importance === 'low'
                            ? 'rgba(144, 202, 249, 0.25)'
                            : 'rgba(100, 181, 246, 0.22)',
                        color:
                          event.importance === 'high'
                            ? '#1e88e5'
                            : event.importance === 'low'
                            ? '#0d47a1'
                            : '#1976d2',
                        border:
                          event.importance === 'high'
                            ? '1px solid rgba(30, 136, 229, 0.4)'
                            : event.importance === 'low'
                            ? '1px solid rgba(144, 202, 249, 0.5)'
                            : '1px solid rgba(100, 181, 246, 0.45)'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {event.description || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex' }}>
                      <Tooltip title="Edit">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => onEditEvent(event)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => onDeleteEvent(event.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, event)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No events found. Add some events to your timeline!
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit Event
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete Event
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default TimelineEventList;
